import type { SlideChangeMethod } from 'Modules/video/components/vm-video-slides-viewer/vm-video-slides-viewer';
import type { SlidesCloseMethod, SlidesOpenSource } from 'Modules/video/components/vm-video-slides/vm-video-slides';

/**
 * Slides viewer Mixpanel events + typed tracker.
 *
 * Slide numbers are 1-based positions in the deck, not the API's `slide_number`.
 * The video context (`video_id`, `video_slug`, `channel_id`, `template_version`) is added automatically.
 */
export const VideoSlidesEvents = {
    MODAL_OPEN: 'SlidesModalOpen',
    PAGE_NAVIGATION: 'SlidesPageNavigation',
    MODAL_CLOSE: 'SlidesModalClose',
} as const;

export type VideoSlidesEventName = (typeof VideoSlidesEvents)[keyof typeof VideoSlidesEvents];

export interface VideoSlidesEventPayloads {
    [VideoSlidesEvents.MODAL_OPEN]: {
        source: SlidesOpenSource;
        slide_count: number;
        video_was_playing: boolean;
        /** `null` when the player isn't ready yet. */
        video_position_s: number | null;
    };
    [VideoSlidesEvents.PAGE_NAVIGATION]: {
        method: SlideChangeMethod;
        from_slide: number;
        to_slide: number;
        slide_id: number;
    };
    [VideoSlidesEvents.MODAL_CLOSE]: {
        method: SlidesCloseMethod;
        duration_ms: number;
        /** Unique slides shown, including the first. */
        slides_viewed: number;
        max_slide_number: number;
        last_slide_number: number;
        /** Playback actually started, not just requested. */
        video_resumed: boolean;
    };
}

export function trackVideoSlidesEvent<E extends VideoSlidesEventName>(
    event: E,
    props: VideoSlidesEventPayloads[E],
): void {
    const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
    if (!context?.videoId) return;
    // Analytics must never break the viewer or player flow.
    try {
        window.vumedi?.mixpanel?.track(event, {
            video_id: context.videoId,
            video_slug: context.videoSlug,
            channel_id: context.channelId,
            template_version: context.templateVersion,
            ...props,
        });
    } catch {
        // Mixpanel reports its own errors.
    }
}
